/**
 * The subscription registry.
 *
 * Two rules shape everything here, and both are protocol decisions rather than
 * client conveniences:
 *
 * **Every control frame is batched.** `subscribe` takes an *array*. At a
 * 60-second period a market-page client swaps four to six channels sixty times
 * an hour — 1,440 times a day per open series — and that must cost one frame in
 * and one frame out, not eight (asyncapi §5.3). One `subscribe` per *intent*,
 * never one per channel, chunked only at the protocol's 50-channel ceiling.
 *
 * **`sub_id`s do not survive a reconnect.** They are server-assigned handles
 * that appear *on* frames so a client can route them; they are never an argument
 * to a control frame — `unsubscribe` names channels — and a new connection
 * starts empty with no session resumption and no `seq` continuation (asyncapi
 * §5.8). So this registry keeps two things apart: the **desired set**, which is
 * the client's intent and survives everything, and the **held set**, which is
 * one socket's reality and is thrown away whole on every reconnect.
 *
 * Partial failure lives inside a successful `ack` (`subscribed[]` + `failed[]`),
 * never as an `error` frame: three good channels must not be lost because a
 * fourth was typo'd. An `error` frame means the whole request was rejected.
 */

import type {
  ChannelName,
  FailedEntry,
  ServerFrame,
  SubscribeAckData,
  SubscribeData,
  SubscribedEntry,
  UnsubscribeAckData,
  WsErrorCode,
} from './protocol'
import { WS_LIMITS } from './protocol'
import { parseChannel } from './channels'
import type { WsConnection } from './connection'

export type SubscriptionState = 'PENDING' | 'ACTIVE'

export interface HeldSubscription {
  channel: ChannelName
  state: SubscriptionState
  /** Server-assigned. Present once the `ack` lands; void after a reconnect. */
  subId?: number
  /** The `seq` the first frame on this subscription will carry — the value the
   *  gap detector anchors on (docs/03 §4.2). */
  anchorSeq?: number
  snapshotPending?: boolean
}

export interface SubscriptionHandlers {
  /** Channels the server confirmed, with their `sub_id` and starting `seq`. */
  onSubscribed?: (entries: SubscribedEntry[]) => void
  /** Channels the server refused, inside an otherwise successful `ack`. */
  onFailed?: (entries: FailedEntry[]) => void
  /** Channels no longer held, for any reason — an unsubscribe we asked for, a
   *  terminal error, or a session expiry. */
  onDropped?: (channels: ChannelName[], reason: DropReason) => void
  /** The client asked for more than the connection allows. Reported rather than
   *  sent: a frame the server will reject with `SUBSCRIPTION_LIMIT` costs a
   *  round trip to learn something the client already knew. */
  onLimitExceeded?: (refused: ChannelName[], limit: number) => void
}

export type DropReason = 'UNSUBSCRIBED' | 'TERMINAL_ERROR' | 'SESSION_EXPIRED' | 'RECONNECT'

export interface SubscriptionsOptions extends SubscriptionHandlers {
  connection: WsConnection
}

export interface SubscribeOptions {
  /**
   * Whether the server should push an in-band `snapshot` on subscribe.
   *
   * Defaults to `true`, and the default matters. The in-band snapshot is what
   * makes the roll cost zero round trips: the client subscribes `book.{N+1}`
   * seventeen to twenty-two seconds early, receives an empty ladder with its own
   * `seq`, and at `T+73` the market maker's first quotes arrive as deltas that
   * apply to a snapshot it has been holding since `T+45` (docs/03 §6.4). It is
   * also the only anchor that is correct under either reading of `seq`
   * (register §F15a) — a REST body carries no `sub_id` and cannot say where a
   * per-subscription counter starts.
   */
  snapshot?: boolean
  /** `book` only. Defaults to `config.ws.book_depth_default`. */
  depth?: number
}

interface PendingRequest {
  id: string
  kind: 'subscribe' | 'unsubscribe'
  channels: ChannelName[]
}

/** Failures that are races, not faults: the client asked for something the
 *  server had already done or already undone. Neither costs a channel. */
const BENIGN_FAILURES: ReadonlySet<WsErrorCode> = new Set<WsErrorCode>([
  'ALREADY_SUBSCRIBED',
  'NOT_SUBSCRIBED',
])

/**
 * Failures that mean "not now", never "not ever".
 *
 * The distinction is between the two sets this class keeps: `#held` is one
 * socket's reality and `#desired` is the client's intent. A failure that says
 * something about *authorisation* says nothing about intent — the page still
 * wants the channel, and will get it the moment the connection is authorised —
 * so it must clear `#held` and leave `#desired` alone.
 *
 * `asyncapi`'s own table is decisive for the first: `UNAUTHENTICATED` is
 * "**terminal for the subscription: no** — private channel on an anonymous
 * connection". Deleting it from `#desired` was therefore contrary to the
 * contract as well as wrong in effect, and the effect is the bad part: after a
 * re-`auth`, `resync()` sends the desired set, which no longer names them, so
 * the private channels never come back and nothing anywhere says why.
 *
 * `SESSION_EXPIRED` is terminal for the subscription and explicitly *not* for
 * the socket — "private subscriptions dropped, socket kept" — and docs/04 §4.3
 * says to "re-`auth` on the same connection". That is the same shape: the
 * subscription is gone, the intent is not.
 *
 * Register §F23a.
 */
const REAUTHABLE_FAILURES: ReadonlySet<WsErrorCode> = new Set<WsErrorCode>([
  'UNAUTHENTICATED',
  'SESSION_EXPIRED',
])

export class Subscriptions {
  readonly #connection: WsConnection
  readonly #handlers: SubscriptionHandlers

  /** What the client wants to hold. Survives reconnects; it *is* the intent. */
  readonly #desired = new Set<ChannelName>()
  /** What this socket actually holds. Cleared on every reconnect. */
  readonly #held = new Map<ChannelName, HeldSubscription>()
  readonly #pending = new Map<string, PendingRequest>()

  #subscriptionCount = 0

  constructor(options: SubscriptionsOptions) {
    this.#connection = options.connection
    this.#handlers = options
  }

  get desiredChannels(): ChannelName[] {
    return [...this.#desired]
  }

  get heldChannels(): ChannelName[] {
    return [...this.#held.keys()]
  }

  /** The server's own count, from the last `ack`. Worth comparing against
   *  `heldChannels.length`: a drift means a drop the client did not see. */
  get serverSubscriptionCount(): number {
    return this.#subscriptionCount
  }

  get pendingRequests(): number {
    return this.#pending.size
  }

  get(channel: ChannelName): HeldSubscription | undefined {
    return this.#held.get(channel)
  }

  /** Route a data frame to its subscription by name. `sub_id` is deliberately
   *  not the key — it changes across reconnects and the channel name does not. */
  isActive(channel: ChannelName): boolean {
    return this.#held.get(channel)?.state === 'ACTIVE'
  }

  // -------------------------------------------------------------------------

  /**
   * Add channels to the desired set and send one batched `subscribe` per 50.
   *
   * Channels already held or already in flight are dropped from the request
   * rather than re-sent: a duplicate subscribe answers `ALREADY_SUBSCRIBED` in
   * `failed[]`, which is harmless but is also a round trip spent learning
   * something the registry knew.
   */
  subscribe(channels: readonly ChannelName[], options: SubscribeOptions = {}): string[] {
    const fresh = channels.filter((c) => !this.#held.has(c))
    for (const channel of channels) this.#desired.add(channel)
    if (fresh.length === 0) return []

    const limit = this.#connection.config.max_subscriptions_per_connection
    const headroom = Math.max(0, limit - this.#held.size)
    const accepted = fresh.slice(0, headroom)
    const refused = fresh.slice(headroom)
    if (refused.length > 0) {
      for (const channel of refused) this.#desired.delete(channel)
      this.#handlers.onLimitExceeded?.(refused, limit)
    }
    if (accepted.length === 0) return []

    for (const channel of accepted) this.#held.set(channel, { channel, state: 'PENDING' })
    return this.#sendBatched('subscribe', accepted, options)
  }

  /**
   * Drop channels from the desired set and send one batched `unsubscribe` per
   * 50. Names channels — never `sub_id`s.
   *
   * The channel leaves `#held` **when the frame is sent**, not when the ack
   * comes back. Waiting for the ack would break the recovery path, which
   * unsubscribes and immediately resubscribes: with the channel still marked
   * held, `subscribe()` would filter it out as already-held and send nothing,
   * and the ack arriving a round trip later would then remove the last record
   * that the client ever wanted it. The channel would be silently gone until the
   * next reconnect — invisible in a synchronous test and certain in production.
   */
  unsubscribe(channels: readonly ChannelName[]): string[] {
    const known = channels.filter((c) => this.#desired.has(c) || this.#held.has(c))
    for (const channel of channels) {
      this.#desired.delete(channel)
      this.#held.delete(channel)
    }
    if (known.length === 0) return []
    return this.#sendBatched('unsubscribe', known)
  }

  /**
   * Everything the client wants, in one go.
   *
   * The roll is one `unsubscribe` and one `subscribe`, and an asset switch is a
   * single batched frame each way at the same anchor so the countdown does not
   * jump (docs/03 §5.5). This is that operation: diff the target against the
   * desired set and send at most one frame of each kind per 50 channels.
   */
  setDesired(channels: readonly ChannelName[], options: SubscribeOptions = {}): void {
    const target = new Set(channels)
    const toDrop = [...this.#desired].filter((c) => !target.has(c))
    const toAdd = [...target].filter((c) => !this.#desired.has(c))
    if (toDrop.length > 0) this.unsubscribe(toDrop)
    if (toAdd.length > 0) this.subscribe(toAdd, options)
  }

  #sendBatched(
    kind: 'subscribe' | 'unsubscribe',
    channels: readonly ChannelName[],
    options: SubscribeOptions = {},
  ): string[] {
    const ids: string[] = []
    for (let i = 0; i < channels.length; i += WS_LIMITS.maxChannelsPerFrame) {
      const chunk = channels.slice(i, i + WS_LIMITS.maxChannelsPerFrame)
      const data: SubscribeData =
        kind === 'subscribe'
          ? {
              channels: [...chunk],
              snapshot: options.snapshot ?? true,
              ...(this.#depthFor(chunk, options) === undefined
                ? {}
                : { depth: this.#depthFor(chunk, options) as number }),
            }
          : { channels: [...chunk] }

      const id = this.#connection.send(kind, data)
      if (id === null) {
        // No socket. The desired set already records the intent, and `resync()`
        // sends it in full the moment one exists — which is the same path a
        // reconnect takes, so there is only one resubscribe implementation.
        if (kind === 'subscribe') for (const channel of chunk) this.#held.delete(channel)
        continue
      }
      this.#pending.set(id, { id, kind, channels: [...chunk] })
      ids.push(id)
    }
    return ids
  }

  /** `depth` applies to `book` only, so it is sent only when the chunk carries
   *  one — a `depth` on a frame of tickers is noise the server has to ignore. */
  #depthFor(channels: readonly ChannelName[], options: SubscribeOptions): number | undefined {
    const hasBook = channels.some((c) => parseChannel(c).kind === 'book')
    if (!hasBook) return undefined
    const requested = options.depth ?? this.#connection.config.book_depth_default
    return Math.min(requested, this.#connection.config.book_depth_max)
  }

  // -------------------------------------------------------------------------

  /**
   * Apply a control `ack`.
   *
   * Returns the entries so the sequencer can anchor on the starting `seq` in the
   * same turn the subscription becomes active — a frame that arrives between the
   * ack and the anchor would otherwise look like a gap.
   */
  applyAck(frame: ServerFrame<SubscribeAckData | UnsubscribeAckData>): {
    subscribed: SubscribedEntry[]
    unsubscribed: ChannelName[]
    failed: FailedEntry[]
  } {
    const data = frame.data
    const request = typeof frame.id === 'string' ? this.#pending.get(frame.id) : undefined
    if (request) this.#pending.delete(request.id)

    const subscribed = (data && 'subscribed' in data ? data.subscribed : []) ?? []
    const unsubscribed = (data && 'unsubscribed' in data ? data.unsubscribed : []) ?? []
    const failed = data?.failed ?? []
    if (data && typeof data.subscription_count === 'number') {
      this.#subscriptionCount = data.subscription_count
    }

    for (const entry of subscribed) {
      this.#held.set(entry.channel, {
        channel: entry.channel,
        state: 'ACTIVE',
        subId: entry.sub_id,
        anchorSeq: entry.seq,
        ...(entry.snapshot_pending === undefined ? {} : { snapshotPending: entry.snapshot_pending }),
      })
    }
    for (const channel of unsubscribed) this.#held.delete(channel)

    const lost: ChannelName[] = []
    const reauthable: ChannelName[] = []
    for (const entry of failed) {
      const channel = entry.channel as ChannelName
      if (BENIGN_FAILURES.has(entry.code)) {
        // `ALREADY_SUBSCRIBED`: the server's existing stream and its `seq` are
        // untouched, so the channel stays held — but this client has no `sub_id`
        // or anchor for it, and inventing one would fake a gap detector. Leave
        // it pending; the next reconnect resubscribes it cleanly.
        if (entry.code === 'NOT_SUBSCRIBED') this.#held.delete(channel)
        continue
      }
      if (REAUTHABLE_FAILURES.has(entry.code)) {
        // The connection is not authorised for this yet. `#desired` survives, so
        // `resubscribeMissing()` after the next `auth` ack — or `resync()` after
        // the next reconnect — restores it without the caller having to
        // remember it wanted it.
        this.#held.delete(channel)
        reauthable.push(channel)
        continue
      }
      this.#held.delete(channel)
      this.#desired.delete(channel)
      lost.push(channel)
    }

    if (subscribed.length > 0) this.#handlers.onSubscribed?.(subscribed)
    if (failed.length > 0) this.#handlers.onFailed?.(failed)
    if (unsubscribed.length > 0) this.#handlers.onDropped?.(unsubscribed, 'UNSUBSCRIBED')
    if (reauthable.length > 0) this.#handlers.onDropped?.(reauthable, 'SESSION_EXPIRED')
    if (lost.length > 0) this.#handlers.onDropped?.(lost, 'TERMINAL_ERROR')

    return { subscribed, unsubscribed, failed }
  }

  /**
   * Channels the server has dropped underneath us — a terminal `error` frame's
   * `unsubscribed[]`.
   *
   * They stay in the **desired** set. `SESSION_EXPIRED` is followed by a re-auth
   * and a resubscribe of exactly these channels, and `SLOW_CONSUMER` is followed
   * by a re-snapshot and a resubscribe of the one it named; in both cases the
   * client still wants them, so forgetting the intent would silently shrink the
   * page.
   */
  markDropped(channels: readonly ChannelName[], reason: DropReason): void {
    const dropped: ChannelName[] = []
    for (const channel of channels) {
      if (this.#held.delete(channel)) dropped.push(channel)
    }
    if (dropped.length > 0) this.#handlers.onDropped?.(dropped, reason)
  }

  /**
   * A new socket. Every `sub_id` and every `seq` from the old one is void.
   *
   * Called on `open`, not on `close`: between the two the desired set is the
   * only truthful record of what the page is showing, and a client that cleared
   * it on close would reconnect to a blank page.
   */
  resync(options: SubscribeOptions = {}): string[] {
    const wanted = [...this.#desired]
    const previouslyHeld = [...this.#held.keys()]
    this.#held.clear()
    this.#pending.clear()
    this.#subscriptionCount = 0
    if (previouslyHeld.length > 0) this.#handlers.onDropped?.(previouslyHeld, 'RECONNECT')
    if (wanted.length === 0) return []

    for (const channel of wanted) this.#held.set(channel, { channel, state: 'PENDING' })
    return this.#sendBatched('subscribe', wanted, options)
  }

  /**
   * Ask again for everything wanted but not held, on the socket as it is now.
   *
   * The counterpart to `REAUTHABLE_FAILURES`, and the reason keeping `#desired`
   * is worth anything: a connection that has just been authorised can serve
   * channels it refused a moment ago, and nothing else would ever ask for them
   * again. `resync()` is the reconnect's version of this and throws `#held`
   * away first; this one runs on a socket that is still up and touches only the
   * gap between intent and reality.
   *
   * Idempotent and free when there is no gap — `subscribe()` filters out
   * anything already held, so calling it on every `auth` ack sends nothing in
   * the normal case.
   */
  resubscribeMissing(options: SubscribeOptions = {}): string[] {
    const missing = [...this.#desired].filter((channel) => !this.#held.has(channel))
    return missing.length === 0 ? [] : this.subscribe(missing, options)
  }

  /** Forget everything. The tab is going away. */
  clear(): void {
    this.#desired.clear()
    this.#held.clear()
    this.#pending.clear()
    this.#subscriptionCount = 0
  }
}
